import { motion } from "framer-motion";
import { Reveal, SectionLabel, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { CAPABILITIES, IMAGES } from "@/lib/content";

export default function Product() {
    return (
        <section
            id="product"
            className="relative mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32"
            data-testid="product"
        >
            <Reveal>
                <SectionLabel index="05">Product</SectionLabel>
            </Reveal>

            <div className="grid gap-14 md:grid-cols-12 md:items-center">
                <div className="md:col-span-6">
                    <Reveal delay={0.1}>
                        <h2 className="font-display text-3xl md:text-5xl font-light leading-tight tracking-tight text-white">
                            An intelligent inheritance platform that makes the
                            first step simple, and the long-term relationship{" "}
                            <span className="italic text-gold">powerful.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-8 text-base md:text-lg leading-relaxed text-white/70">
                            For $88, a person starts a guided conversation with
                            HEIR. In roughly an hour they create a clear, protected
                            legacy plan covering family, assets, values, and
                            wishes. That single step begins a relationship that can
                            last generations.
                        </p>
                    </Reveal>
                </div>

                {/* Spotlight product shot — clipped frame */}
                <div className="md:col-span-6">
                    <Reveal delay={0.15}>
                        <div className="relative">
                            <div className="absolute -inset-4 bg-gold/[0.06] blur-2xl" />
                            <motion.div
                                whileHover={{ y: -6 }}
                                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                className="relative overflow-hidden border border-white/10"
                            >
                                <img
                                    src={IMAGES.product}
                                    alt="HEIR guided experience"
                                    className="h-full w-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-navy-950/60 to-transparent" />
                                <div className="absolute bottom-4 left-4 border border-gold/40 bg-navy-950/70 px-4 py-2 backdrop-blur-sm">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                                        $88 Guided Entry
                                    </span>
                                </div>
                            </motion.div>
                        </div>
                    </Reveal>
                </div>
            </div>

            {/* Capability cards */}
            <StaggerGroup className="mt-20 grid gap-px border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-3">
                {CAPABILITIES.map((cap) => {
                    const Icon = cap.icon;
                    return (
                        <StaggerItem key={cap.title}>
                            <div className="group h-full bg-navy-950 p-8 transition-colors duration-300 hover:bg-navy-900">
                                <Icon
                                    className="h-7 w-7 text-gold transition-transform duration-500 group-hover:scale-110"
                                    strokeWidth={1.25}
                                />
                                <h3 className="mt-6 font-display text-xl font-light text-white">
                                    {cap.title}
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-white/60">
                                    {cap.body}
                                </p>
                            </div>
                        </StaggerItem>
                    );
                })}
                {/* Closing statement cell */}
                <StaggerItem>
                    <div className="flex h-full flex-col justify-center bg-gold/[0.05] p-8">
                        <p className="font-display text-lg font-light italic leading-snug text-white">
                            The $88 conversation is not the product. It is the
                            beginning of a relationship that can last{" "}
                            <span className="text-gold">30–50 years.</span>
                        </p>
                    </div>
                </StaggerItem>
            </StaggerGroup>
        </section>
    );
}
